
import { supabase } from "@/integrations/supabase/client";

export const optimizedFetchPlanRoutines = async (planId: string) => {
  try {
    // Step 1: Get routine IDs ordered by position (for sequential mode)
    const { data: routineLinks, error: linksError } = await supabase
      .from("workout_plan_routines")
      .select("routine_id, position")
      .eq("workout_plan_id", planId)
      .order("position", { ascending: true })
      .limit(20);

    if (linksError) throw linksError;
    if (!routineLinks?.length) return [];

    const routineIds = routineLinks.map(link => link.routine_id);
    const positionMap = new Map(routineLinks.map(link => [link.routine_id, link.position ?? 0]));

    // Step 2: Get routines with limited fields
    const { data: routines, error: routinesError } = await supabase
      .from("routines")
      .select("id, name, image_url")
      .in("id", routineIds)
      .limit(20);

    if (routinesError) throw routinesError;

    // Step 3: Get routine days separately (more efficient)
    const { data: routineDays, error: daysError } = await supabase
      .from("routine_days")
      .select("routine_id, day")
      .in("routine_id", routineIds);

    if (daysError) throw daysError;

    // Step 4: Get exercises separately (batch processing)
    const { data: exercises, error: exercisesError } = await supabase
      .from("exercises")
      .select(`
        id,
        name,
        sets,
        reps,
        weight,
        routine_id,
        position,
        set_data,
        exercise_rest_time,
        set_rest_time,
        video_url
      `)
      .in("routine_id", routineIds)
      .order("position", { ascending: true })
      .limit(200);

    if (exercisesError) throw exercisesError;

    // Combine data efficiently
    const routinesWithData = routines?.map(routine => {
      const routineDayData = routineDays?.find(rd => rd.routine_id === routine.id);
      const routineExercises = exercises?.filter(ex => ex.routine_id === routine.id) || [];

      const formattedExercises = routineExercises.map(exercise => ({
        ...exercise,
        exercise_rest_time: exercise.exercise_rest_time !== null ? Number(exercise.exercise_rest_time) : null,
        set_rest_time: exercise.set_rest_time !== null ? Number(exercise.set_rest_time) : null,
        set_data: exercise.set_data || []
      }));

      return {
        ...routine,
        day: routineDayData?.day || null,
        position: positionMap.get(routine.id) ?? 0,
        exercises: formattedExercises
      };
    });

    // Sort routines by stored position so sequential mode reflects DB order
    const sorted = (routinesWithData || []).sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0)
    );

    return sorted;
  } catch (error) {
    console.error("Error in optimizedFetchPlanRoutines:", error);
    throw error;
  }
};
